/**
 * SwingTiles.getInlineData: clone panel getFullData + fill-row scalars + `{name}__rowNum`.
 * Не dump всей формы; PK-only overlay для tilesRowScope (клоны панели) сюда не относится.
 */
import { IKey } from '@/common/constants/ikey'
import { convertRowDataToFieldsForScript } from '@/common/utils/rowDataUtils'

function readLayoutPosition(action: unknown): string {
  if (action == null || typeof action !== 'object') return ''
  const record = action as Record<string, unknown>
  if (typeof record.getLayoutPosition === 'function') {
    return String((record.getLayoutPosition as () => unknown)() ?? '')
      .trim()
      .toLowerCase()
  }
  const direct = record.layoutPosition
  if (direct != null && String(direct).trim() !== '') {
    return String(direct).trim().toLowerCase()
  }
  const nestedData = record.data
  const layout =
    record.layout ??
    (nestedData && typeof nestedData === 'object'
      ? (nestedData as { layout?: unknown }).layout
      : undefined)
  if (!layout || typeof layout !== 'object') return ''
  const raw = (layout as { position?: unknown }).position
  if (raw == null) return ''
  if (typeof raw === 'object' && raw !== null && '_text' in raw) {
    return String((raw as { _text?: unknown })._text ?? '')
      .trim()
      .toLowerCase()
  }
  return String(raw).trim().toLowerCase()
}

/** Swing `IKey.INLINE.equals(action.getLayoutPosition())`. */
export function isTilesInlineLayoutAction(action?: unknown): boolean {
  return readLayoutPosition(action) === 'inline'
}

export function buildTilesInlineRowNumKey(tableFieldName: string): string {
  const table = String(tableFieldName ?? '').trim()
  if (!table) return ''
  return `${table}__${IKey.ROW_NUM}`
}

/**
 * INLINE currentData Tiles: поля клона (HTML `content` и т.п.) + скаляры строки UniDataTable + rowNum.
 * Скаляры строки перекрывают одноимённые ключи клона (fill rData).
 */
export function buildTilesInlineGetDataFields(options: {
  tableFieldName: string
  rowIndex: number
  rowData?: unknown
  clonePanelFields?: Record<string, unknown> | null
}): Record<string, unknown> {
  const clone =
    options.clonePanelFields && typeof options.clonePanelFields === 'object' && !Array.isArray(options.clonePanelFields)
      ? { ...options.clonePanelFields }
      : {}
  const rowScalars = convertRowDataToFieldsForScript(options.rowData) as Record<string, unknown>
  const out: Record<string, unknown> = { ...clone, ...rowScalars }
  const rowNumKey = buildTilesInlineRowNumKey(options.tableFieldName)
  const index = Number(options.rowIndex)
  if (rowNumKey && Number.isFinite(index) && index >= 0) {
    out[rowNumKey] = { type: 'Long', value: index }
  }
  return out
}

/** selectedData для INLINE Tiles: typed `fields` + плоские примитивы (filterClientData allowed keys). */
export function mergeTilesInlineSelectedData(
  rowData: Record<string, unknown>,
  tableFieldName: string,
  rowIndex: number,
  clonePanelFields?: Record<string, unknown> | null,
): Record<string, unknown> {
  const inlineFields = buildTilesInlineGetDataFields({
    tableFieldName,
    rowIndex,
    rowData,
    clonePanelFields,
  })
  const next: Record<string, unknown> = { ...rowData }
  const existingFields =
    rowData.fields && typeof rowData.fields === 'object' && !Array.isArray(rowData.fields)
      ? (rowData.fields as Record<string, unknown>)
      : {}
  next.fields = { ...existingFields, ...inlineFields }
  for (const [key, typed] of Object.entries(inlineFields)) {
    const value =
      typed && typeof typed === 'object' && 'value' in typed
        ? (typed as { value: unknown }).value
        : typed
    next[key] = value
  }
  return next
}
